import { useFormContext, useWatch } from 'react-hook-form';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, styled, Typography } from '@mui/material';
import type { PackEntry } from 'types/shared';
import type { AdSetEntry } from '../api/getAdSets';
import { CAMPAIGN_NAME_FILTER } from '../constants';
import type { MarketingProcess } from 'types/shared';
import type { PartnershipCreationMode } from '../types';
import {
  calculateScalingEgAdsCount,
  calculateAdsCount,
  getAdsPerCreativeMultiplier,
} from '../utils/calculateAdsCount';
import { isScalingEgProcess } from '../utils/marketingProcess';

const StyledTypography = styled(Typography)(({ theme, variant }) => ({
  color: 'rgba(57, 32, 128, 1)',
  fontSize:
    variant === 'body1'
      ? theme.typography.body1.fontSize
      : theme.typography.caption.fontSize,
  fontWeight: variant === 'body1' ? 500 : 400,
}));

const CounterBadge = ({
  value,
  label,
}: {
  value: number;
  label: string;
}) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      justifyContent: 'flex-start',
      backgroundColor: theme.palette.info.dark,
      px: 2,
      borderRadius: 1,
    })}
  >
    <StyledTypography variant="body1">{value}</StyledTypography>
    <StyledTypography variant="caption">{label}</StyledTypography>
  </Box>
);

const AdSetAdsCounter: FC = () => {
  const { control } = useFormContext();

  const selectedAdSets = useWatch({ control, name: 'selectedAdSets' }) as AdSetEntry[];
  const duplicateCounts = useWatch({
    control,
    name: 'selectedAdSetsDuplicatesCount',
  }) as Record<string, string>;
  const packsWithCreatives = useWatch({
    control,
    name: 'packsWithCreatives',
  }) as PackEntry[];
  const selectedCreatives = useWatch({
    control,
    name: 'selectedCreatives',
  }) as string[];
  const marketingProcess = useWatch({ control, name: 'marketingProcess' }) as string;
  const partnershipAdsEnabled = useWatch({
    control,
    name: 'partnershipAdsEnabled',
  }) as boolean;
  const partnershipAdSettings = useWatch({
    control,
    name: 'partnershipAdSettings',
  }) as { partnershipId?: string }[] | undefined;
  const partnershipCreationMode = useWatch({
    control,
    name: 'partnershipCreationMode',
  }) as PartnershipCreationMode | undefined;

  const isMarketingProcessInbox = useMemo(
    () => CAMPAIGN_NAME_FILTER[marketingProcess as MarketingProcess]?.includes('inbox'),
    [marketingProcess],
  );

  const adsPerCreative = useMemo(
    () =>
      getAdsPerCreativeMultiplier(
        partnershipAdsEnabled,
        partnershipAdSettings,
        partnershipCreationMode,
      ),
    [partnershipAdsEnabled, partnershipAdSettings, partnershipCreationMode],
  );

  const totalAds = useMemo(
    () => {
      if (isScalingEgProcess(marketingProcess)) {
        return calculateScalingEgAdsCount({
          selectedCreatives,
          selectedAdSets,
          adsPerCreative,
        });
      }

      return calculateAdsCount({
        packsWithCreatives,
        selectedAdSets,
        duplicateCounts,
        isMarketingProcessInbox,
        adsPerCreative,
      });
    },
    [
      adsPerCreative,
      duplicateCounts,
      isMarketingProcessInbox,
      marketingProcess,
      packsWithCreatives,
      selectedCreatives,
      selectedAdSets,
    ],
  );

  if (!selectedAdSets?.length) {
    return null;
  }

  return <CounterBadge value={totalAds} label="Ads" />;
};

export default AdSetAdsCounter;
